import {
  toolCallResultSchema,
  ToolCallResultStatus,
} from 'common/src/modules/creativeTemplates/copilot/copilotCreativeTemplates.consts';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import invokeStandaloneLLMCall from 'common/src/modules/shared/copilot/utils/invokeStandaloneLLMCall';
import {z} from 'zod';
import {transformAnimationSchema} from '../adBuilderAgent/adBuilderAgent.schemas';

// Type for the parsed transform parameters
type TransformParams = z.infer<typeof transformAnimationSchema>;

export interface TransformParseResult {
  transformParams: TransformParams;
  unsupportedRequest?: string;
}

export async function parseTransformFromNaturalLanguage(
  transformExplained: string,
  compWidth: number,
  compHeight: number,
  abortSignal: AbortSignal,
): Promise<TransformParseResult> {
  const systemPrompt = [
    'You are requested to receive a natural language description of a layer transform (inside an ad composition) and convert it into structured animation transform parameters and call the tool.',
    'IMPORTANT: Only call the tool once with all transform changes. Do not provide any text response.',
  ].join(' ');

  const userPrompt = `Composition size: ${compWidth}x${compHeight} pixels

Transform request: "${transformExplained}"

Convert this natural language description into structured transform parameters and call the tool.
Consider:
- Do Not send to tool any transform property which does not require changing.
- For static changes, use time: 0. For animations, create multiple keyframes with appropriate timing.
- Position/Scale/Anchor: z is optional for 3D transforms. for 2D rotation, use rotationZ
- Rotation X/Y/Z and Opacity: they are all 1D values, so use must use the x value in keyframes only`;
  const response = await invokeStandaloneLLMCall({
    messages: [openAISystemMessage(systemPrompt), openAIUserMessage(userPrompt)],
    responseSchema: toolCallResultSchema.extend({
      result: transformAnimationSchema,
    }),
    buildResponseUsingToolCall: true,
    abortSignal,
  });

  // Check if LLM indicated failure
  if (response.status === ToolCallResultStatus.Failure) {
    const reason = response.failureReason || 'Request failed without specific reason';
    throw new Error(`Transform edit failed: ${reason}`);
  }

  return {
    transformParams: response.result,
    ...(response.unsupportedRequestPart && {unsupportedRequest: response.unsupportedRequestPart}),
  };
}
